import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import type { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { useIsNativeEmbed } from '../../../navigation/hooks/useIsNativeEmbed';
import { offersCoverEditor } from '../../../../coverEditor/entry/coverFieldState';
import { useOpenCoverEditor } from '../../../../coverEditor/entry/useOpenCoverEditor';
import { KitSlotView } from './kitSlots';
import { catalogArtworkSrc } from '../../../../smartLinks/catalog/catalogArtwork';
import { KitSlotThumb } from './KitSlotThumb';
import { KitStateChip } from './KitStateChip';
import { kitMessages } from './kitMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Kit.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface KitCoverSlotProps {
  accountId: number;
  item: CatalogItem;
  view: KitSlotView;
  canAct: boolean;
}

/** The size of the slot picture in CSS px. */
const SLOT_PICTURE_PX = 160;

/** The song cover for Download: the original file of the row, else the url of the claim. */
function coverUrl(item: CatalogItem): string | null {
  if (item.artworkFile !== null) {
    return getFullAPIFileURL(item.artworkFile.url);
  }
  return item.artworkUrl;
}

/**
 * The cover slot (design assets-rethink b1): the cover of the song with "Edit cover" or "Design a
 * cover" (the cover editor, like the song page cover field) and Download. The native web view does
 * not offer the editor (`offersCoverEditor`).
 */
const KitCoverSlotComponent: React.FC<KitCoverSlotProps> = ({ accountId, item, view, canAct }) => {
  const intl = useIntl();
  const hasEditor = offersCoverEditor({ isNativeEmbed: useIsNativeEmbed() });
  const openEditor = useOpenCoverEditor({ accountId });
  const url = coverUrl(item);
  const name = intl.formatMessage(kitMessages.slotCover);
  return (
    <div className={clsx(styles.slot, view.state !== 'made' && styles.slotEmpty)} data-testid="kit-slot-cover">
      <KitSlotThumb view={view} name={name} pictureUrl={catalogArtworkSrc(item, SLOT_PICTURE_PX) ?? null} />
      <div className={styles.body}>
        <div className={styles.name}>
          <b>{name}</b>
          <KitStateChip state={view.state} />
        </div>
        <span className={styles.line}>
          {intl.formatMessage(view.state === 'made' ? kitMessages.coverLine : kitMessages.coverNone)}
        </span>
      </div>
      <div className={styles.foot}>
        {canAct && hasEditor && (
          <button type="button" className={styles.link} onClick={() => openEditor(item.uuid)}>
            {intl.formatMessage(view.state === 'made' ? kitMessages.editCover : kitMessages.designCover)}
          </button>
        )}
        <span className={styles.grow} />
        {url !== null && (
          <a className={styles.link} href={url} download target="_blank" rel="noreferrer">
            {intl.formatMessage(kitMessages.download)}
          </a>
        )}
      </div>
    </div>
  );
};

export const KitCoverSlot = TypedMemo(KitCoverSlotComponent);
